<template>
  <AppForm
    class="space-y-2"
    @submit="onSubmit"
  >
    <TextareaField
      v-model="addressText"
      name="emails"
      :rows="2"
      :label="$t('common.legalAgreement.emailLabel')"
      :help="$t('common.legalAgreement.emailHelp', { max: LEGAL_AGREEMENT_EMAIL_MAX_RECIPIENTS })"
      :server-error="fieldProblem"
      :disabled="isEmailing"
    />

    <div
      v-if="actionProblem !== ''"
      role="alert"
      data-testid="legal-agreement-action-problem"
    >
      <UAlert
        color="error"
        variant="subtle"
        :description="actionProblem"
      />
    </div>

    <p
      v-if="successMessage !== ''"
      class="text-sm text-success"
      role="status"
      data-testid="legal-agreement-email-success"
    >
      {{ successMessage }}
    </p>

    <UButton
      type="submit"
      color="neutral"
      variant="outline"
      icon="i-lucide-mail"
      :loading="isEmailing"
      :disabled="isCoolingDown"
    >
      {{ $t('common.legalAgreement.emailAction') }}
    </UButton>
  </AppForm>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import UAlert from '@nuxt/ui/components/Alert.vue'
import UButton from '@nuxt/ui/components/Button.vue'
import AppForm from '~/design-system/molecules/AppForm.molecule.vue'
import TextareaField from '~/design-system/molecules/TextareaField.molecule.vue'
import { usePresentationTranslator } from '~/composables/usePresentationTranslator'
import { useLegalAgreementEmail } from '~/composables/useLegalAgreementEmail'
import { LEGAL_AGREEMENT_EMAIL_MAX_RECIPIENTS } from '~/constants/legalAgreementEmail'
import { splitEmailEntries } from '~/utils/splitEmailEntries'

const props = defineProps<{
  /** Identifier of the legal agreement that is emailed. */
  legalAgreementId: string
}>()

const {
  actionProblem,
  entryProblems,
  isCoolingDown,
  isEmailing,
  listProblem,
  send,
  successMessage
} = useLegalAgreementEmail(() => props.legalAgreementId)

const t = usePresentationTranslator()
const fieldProblem = computed(() =>
  [
    listProblem.value,
    ...entryProblems.value.map((entryProblem) =>
      t('common.legalAgreement.errors.entry', {
        email: entryProblem.email,
        message: entryProblem.message
      })
    )
  ]
    .filter((message) => message !== '')
    .join(' ')
)

const addressText = ref('')

const onSubmit = () => send(splitEmailEntries(addressText.value))
</script>
